import { Client, Room } from 'colyseus.js'

// Determine WebSocket endpoint based on environment
const getEndpoint = () => {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.hostname
    const port = import.meta.env.DEV ? 2567 : window.location.port || (protocol === 'wss:' ? 443 : 80)
    return `${protocol}//${host}:${port}`
  }
  return 'ws://localhost:2567'
}

class NetworkClient {
  private client: Client
  private lobbyRoom: Room | null = null
  private gameRoom: Room | null = null

  constructor() {
    this.client = new Client(getEndpoint())
  }

  // Connect to lobby for room listing
  async joinLobby(nickname: string): Promise<Room> {
    try {
      this.lobbyRoom = await this.client.joinOrCreate('lobby', { nickname })
      return this.lobbyRoom
    } catch (error) {
      console.error('Failed to join lobby:', error)
      throw error
    }
  }

  leaveLobby() {
    if (this.lobbyRoom) {
      this.lobbyRoom.leave()
      this.lobbyRoom = null
    }
  }

  // Create a new game room
  async createRoom(roomName: string, nickname: string, role: string): Promise<void> {
    if (!this.lobbyRoom) {
      throw new Error('Not connected to lobby')
    }

    return new Promise((resolve, reject) => {
      this.lobbyRoom!.send('create_room', { roomName, nickname, role })

      let handled = false
      this.lobbyRoom!.onMessage('room_created', async (data: { roomId: string; nickname: string; role: string }) => {
        if (handled) return
        handled = true
        try {
          await this.joinGameRoom(data.roomId, data.nickname, data.role)
          resolve()
        } catch (error) {
          reject(error)
        }
      })

      this.lobbyRoom!.onMessage('error', (data) => {
        if (!handled) reject(new Error(data.message))
      })
    })
  }

  // Join an existing game room
  async joinGameRoom(roomId: string, nickname: string, role: string): Promise<Room> {
    try {
      this.leaveLobby()
      this.gameRoom = await this.client.joinById(roomId, { nickname, role })
      return this.gameRoom
    } catch (error) {
      console.error('Failed to join game room:', error)
      throw error
    }
  }

  // Direct create/join for quick play
  async quickJoin(nickname: string, role: string): Promise<Room> {
    try {
      this.gameRoom = await this.client.joinOrCreate('game', {
        nickname,
        role,
        roomName: `Gra ${nickname}`
      })
      return this.gameRoom
    } catch (error) {
      console.error('Failed to quick join:', error)
      throw error
    }
  }

  // Get available rooms
  async getAvailableRooms(): Promise<any[]> {
    try {
      return await this.client.getAvailableRooms('game')
    } catch (error) {
      console.error('Failed to get rooms:', error)
      return []
    }
  }

  getGameRoom(): Room | null {
    return this.gameRoom
  }

  getLobbyRoom(): Room | null {
    return this.lobbyRoom
  }

  leaveGame() {
    if (this.gameRoom) {
      this.gameRoom.leave()
      this.gameRoom = null
    }
  }

  // Send player movement
  sendMove(x: number, y: number) {
    if (this.gameRoom) {
      this.gameRoom.send('move', { x, y })
    }
  }

  // Toggle ready state
  toggleReady() {
    if (this.gameRoom) {
      this.gameRoom.send('toggle_ready')
    }
  }

  // Request game start (host only)
  startGame() {
    if (this.gameRoom) {
      this.gameRoom.send('start_game')
    }
  }

  // Change role
  changeRole(role: string) {
    if (this.gameRoom) {
      this.gameRoom.send('change_role', { role })
    }
  }
}

// Singleton instance
export const networkClient = new NetworkClient()
